import React, { useState, useEffect } from 'react';
import ExcelDropZone from './components/ExcelDropZone';
import ImportPreview from './components/ImportPreview';
import ContactsTable from './components/ContactsTable';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import the CSS
import { processExcelFile } from './controller/import-controller';
import { fetchExistingClients } from './controller/import-controller';

const ImportPage = () => {
  const [importedData, setImportedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [currentView, setCurrentView] = useState('upload'); // 'upload', 'preview', 'table'
  const [fileName, setFileName] = useState('');
  const [isImportingClients, setIsImportingClients] = useState(false);

  // Keep the existing file upload logic unchanged
  const handleFileUpload = async (file) => {
    setIsLoading(true);
    setError(null);
    setFileName(file.name);
    
    try {
      const data = await processExcelFile(file);
      setImportedData(data);
      setCurrentView('preview');
      setImportSuccess(true);
    } catch (err) {
      setError(err.message);
      setImportSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmImport = () => {
    // In a real app, this would send data to a backend
    // For now, we'll just switch to the table view
    setCurrentView('table');
  };

  const handleReset = () => {
    setImportedData([]);
    setFileName('');
    setError(null);
    setImportSuccess(false);
    setCurrentView('upload');
  };
  
  // Add the new function to import existing clients
  const handleImportExistingClients = async () => {
    setIsImportingClients(true);
    
    try {
      const result = await fetchExistingClients();
      
      if (result.success && result.data.length > 0) {
        if (currentView === 'table') {
          // Add to existing data in table view
          const newData = [...importedData];
          let addedCount = 0;
          
          // Add only non-duplicates
          result.data.forEach(client => {
            if (!newData.some(existing => existing.phone === client.phone)) {
              newData.push(client);
              addedCount++;
            }
          });
          
          setImportedData(newData);
          toast.success(`${addedCount} clients ajoutés à votre liste !`);
        } else if (importedData.length > 0 && currentView === 'preview') {
          // In preview with existing data - merge
          const newData = [...importedData];
          let addedCount = 0;
          
          result.data.forEach(client => {
            if (!newData.some(existing => existing.phone === client.phone)) {
              newData.push(client);
              addedCount++;
            }
          });
          
          setImportedData(newData);
          toast.success(`${addedCount} clients ajoutés à la prévisualisation !`);
        } else {
          // First import
          setImportedData(result.data);
          setFileName('Clients existants');
          setCurrentView('preview');
          toast.success(`${result.data.length} clients récupérés avec succès !`);
        }
      } else if (result.data.length === 0) {
        toast.info('Aucun client trouvé dans le système.');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error importing clients:', error);
      toast.error('Erreur lors de la récupération des clients.');
    } finally {
      setIsImportingClients(false);
    }
  };

  // Add a helper function to add more contacts when already in table view
  const handleAddMore = () => {
    setCurrentView('upload');
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 w-full mx-auto">
      {/* Page header with added client import button */}
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Import de Contacts</h2>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-3">
          {/* Only show Add More button when in table view */}
          {currentView === 'table' && (
            <button
              type="button"
              onClick={handleAddMore}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Ajouter plus de contacts
            </button>
          )}
          
          {/* Client import button - always visible */}
          <button
            type="button"
            onClick={handleImportExistingClients}
            disabled={isImportingClients}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {isImportingClients ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Importation...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Importer clients existants
              </>
            )}
          </button>
          
          {/* Keep the existing reset button */}
          {currentView !== 'upload' && (
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recommencer
            </button>
          )}
        </div>
      </div>

      {/* Success message when importing contacts */}
      {importSuccess && currentView === 'table' && (
        <div className="mt-4">
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Importation réussie ! Vous avez importé {importedData.length} contacts.
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button 
                    type="button" 
                    className="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600"
                    onClick={() => setImportSuccess(false)}
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content - keep existing views */}
      <div className="mt-6 w-full">
        {currentView === 'upload' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <ExcelDropZone 
              onFileUpload={handleFileUpload} 
              isLoading={isLoading} 
              error={error}
              hasExistingData={importedData.length > 0}
            />
          </div>
        )}

        {currentView === 'preview' && (
          <ImportPreview 
            data={importedData} 
            fileName={fileName}
            onConfirm={handleConfirmImport}
            onCancel={handleReset}
          />
        )}

        {currentView === 'table' && (
          <ContactsTable data={importedData} />
        )}
      </div>
      

    </div>
  );
};

export default ImportPage;